const SCAN_FORM = '#ship-station-scan-form';
const SCAN_INPUT = '#ship-station-scan-input';
const PROMPT = '#ship-station-prompt';
const PRINT_FRAME = 'ship-station-print-frame';

const MESSAGE_TYPES = {
  labeled: 'success',
  already_labeled: 'notice',
};

const focusScanInput = () => {
  const input = document.querySelector(SCAN_INPUT);
  if (!input) return;

  input.focus();
  input.select();
};

const clearPrompt = () => {
  const host = document.querySelector(PROMPT);
  if (host) host.innerHTML = '';
};

const printLabel = async (url) => {
  if (!url) return;

  let pdf = null;

  try {
    const response = await fetch(url, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`label unavailable (${response.status})`);

    pdf = URL.createObjectURL(await response.blob());
  } catch (error) {
    if (window.flashMessages) {
      window.flashMessages.addMessage({
        type: 'error',
        text: 'The label could not be loaded, so nothing was sent to the printer. Open the order and print it by hand.',
      });
    }

    return;
  }

  // A fresh frame every time: a reused one keeps its previous onload, and that
  // handler fires again on any later load - which is how one scan printed twice.
  const stale = document.getElementById(PRINT_FRAME);
  if (stale) stale.remove();

  const frame = document.createElement('iframe');
  frame.id = PRINT_FRAME;
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;width:0;height:0;border:0;';

  frame.onload = () => {
    frame.onload = null;

    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (error) {
      window.open(pdf, '_blank');
    }

    setTimeout(() => URL.revokeObjectURL(pdf), 60000);
  };

  document.body.appendChild(frame);
  frame.src = pdf;
};

const announce = (payload) => {
  const type = MESSAGE_TYPES[payload.status] || 'error';

  if (window.flashMessages) {
    window.flashMessages.addMessage({ type, text: payload.message });
  }
};

const submitScan = (value, { combine, printAnyway } = {}) => {
  const token = document.querySelector('meta[name="csrf-token"]');
  const body = { invoice_number: value };

  if (combine !== undefined) body.combine = combine;
  if (printAnyway) body.print_anyway = true;

  return fetch('/ship_station/scan', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': token ? token.content : '',
    },
    body: JSON.stringify(body),
  }).then((response) => response.json());
};

const askToCombine = (payload, onAnswer) => {
  const host = document.querySelector(PROMPT);
  if (!host) {
    onAnswer(false);
    return;
  }

  host.innerHTML = '';

  const box = document.createElement('div');
  box.className = 'alert alert-warning d-flex align-items-center mb-2';

  const text = document.createElement('span');
  text.textContent = payload.message;

  const combine = document.createElement('button');
  combine.type = 'button';
  combine.className = 'btn btn-sm btn-primary ml-3';
  combine.textContent = 'One box (Enter)';

  const separate = document.createElement('button');
  separate.type = 'button';
  separate.className = 'btn btn-sm btn-outline-secondary ml-2';
  separate.textContent = 'Separate boxes (Esc)';

  const answer = (combined) => {
    clearPrompt();
    onAnswer(combined);
  };

  combine.addEventListener('click', () => answer(true));
  separate.addEventListener('click', () => answer(false));
  box.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') answer(false);
  });

  box.appendChild(text);
  box.appendChild(combine);
  box.appendChild(separate);
  host.appendChild(box);

  combine.focus();
};


const MODAL_ID = 'ship-station-modal';

const closeModal = () => {
  const existing = document.getElementById(MODAL_ID);
  if (existing) existing.remove();
};

// Asks a yes/no question in a modal. Esc, a click outside and Enter on the default
// focus all give the safe answer.
const askInModal = ({
  heading, message, safeText, actionText, actionClass = 'btn-primary',
}, onAnswer) => {
  closeModal();

  const overlay = document.createElement('div');
  overlay.id = MODAL_ID;
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.style.cssText = 'position:fixed;inset:0;z-index:2000;display:flex;align-items:center;'
    + 'justify-content:center;background:rgba(0,0,0,.45);';

  const card = document.createElement('div');
  card.style.cssText = 'background:#fff;border-radius:6px;padding:24px;max-width:440px;width:90%;'
    + 'box-shadow:0 10px 30px rgba(0,0,0,.25);';

  const title = document.createElement('h5');
  title.textContent = heading;
  title.style.cssText = 'margin:0 0 12px;font-weight:600;';

  const body = document.createElement('p');
  body.textContent = message;
  body.style.cssText = 'margin:0 0 20px;';

  const actions = document.createElement('div');
  actions.style.cssText = 'display:flex;justify-content:flex-end;gap:8px;';

  const safe = document.createElement('button');
  safe.type = 'button';
  safe.className = 'btn btn-sm btn-outline-secondary';
  safe.textContent = safeText;

  const action = document.createElement('button');
  action.type = 'button';
  action.className = `btn btn-sm ${actionClass}`;
  action.textContent = actionText;

  const answer = (confirmed) => {
    document.removeEventListener('keydown', onKeydown);
    closeModal();
    onAnswer(confirmed);
  };

  function onKeydown(event) {
    if (event.key === 'Escape') answer(false);
  }

  safe.addEventListener('click', () => answer(false));
  action.addEventListener('click', () => answer(true));
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) answer(false);
  });
  document.addEventListener('keydown', onKeydown);

  actions.appendChild(safe);
  actions.appendChild(action);
  card.appendChild(title);
  card.appendChild(body);
  card.appendChild(actions);
  overlay.appendChild(card);
  document.body.appendChild(overlay);

  // The safe answer takes focus, never the action: a scanner sends Enter of its own
  // accord, and an unattended keystroke must not print or pay for anything.
  safe.focus();
};

const askToReprint = (payload, onAnswer) => askInModal({
  heading: 'This label was already printed',
  message: payload.message,
  safeText: 'Cancel',
  actionText: 'Reprint label',
}, onAnswer);

const askAboutAddress = (payload, onAnswer) => askInModal({
  heading: 'Address not confirmed',
  message: payload.message,
  safeText: 'Fix address',
  actionText: 'Print anyway',
  actionClass: 'btn-warning',
}, onAnswer);

const initShipStationScan = () => {
  const form = document.querySelector(SCAN_FORM);
  if (!form) return;

  let busy = false;
  let modalPending = false;

  focusScanInput();

  const run = (value, options = {}) => {
    busy = true;

    submitScan(value, options)
      .then((payload) => {
        if (payload.status === 'combinable') {
          askToCombine(payload, (combined) => run(value, { ...options, combine: combined }));
          return;
        }

        const input = document.querySelector(SCAN_INPUT);
        if (input) input.value = '';

        if (payload.status === 'already_labeled') {
          modalPending = true;

          askToReprint(payload, (confirmed) => {
            modalPending = false;
            if (confirmed) printLabel(payload.print_url || payload.label_url);
            focusScanInput();
          });

          return;
        }

        if (payload.status === 'address_unconfirmed') {
          modalPending = true;

          askAboutAddress(payload, (printAnyway) => {
            modalPending = false;

            if (printAnyway) {
              run(value, { ...options, printAnyway: true });
              return;
            }

            if (window.flashMessages) {
              window.flashMessages.addMessage({
                type: 'notice',
                text: `Fix the address in the CRM, then scan ${payload.invoice_number || value} again.`,
              });
            }

            focusScanInput();
          });

          return;
        }

        announce(payload);
        printLabel(payload.print_url || payload.label_url);

        focusScanInput();
      })
      .catch(() => {
        if (window.flashMessages) {
          window.flashMessages.addMessage({
            type: 'error',
            text: 'The scan could not be sent. Check your connection and scan again.',
          });
        }

        focusScanInput();
      })
      .finally(() => {
        busy = false;
      });
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    const input = document.querySelector(SCAN_INPUT);
    const value = input ? input.value.trim() : '';

    if (busy || modalPending || !value) return;

    clearPrompt();
    run(value);
  });
};

export { initShipStationScan };
